import { useState } from "react"
import { Link } from "react-router-dom"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Mail, ArrowLeft, ShieldCheck } from "lucide-react"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PublicFooter } from "@/components/lgpd/Footer"

const DPO_EMAIL = "contatoitacondo@gmail.com"

const REQUEST_TYPES = [
  { v: "access", l: "Acesso aos meus dados" },
  { v: "correction", l: "Correção de dados" },
  { v: "deletion", l: "Exclusão / anonimização" },
  { v: "portability", l: "Portabilidade" },
  { v: "revocation", l: "Revogação de consentimento" },
  { v: "opposition", l: "Oposição ao tratamento" },
  { v: "complaint", l: "Reclamação" },
  { v: "other", l: "Outro" },
]

const Encarregado = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", request_type: "access", description: "" })
  const [loading, setLoading] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.full_name || !form.email) {
      toast.error("Preencha nome e e-mail.")
      return
    }
    setLoading(true)
    const { error } = await supabase.from("lgpd_dsar_requests").insert(form as any)
    setLoading(false)
    if (error) {
      toast.error("Erro ao enviar: " + error.message)
    } else {
      toast.success("Solicitação enviada. Responderemos em até 15 dias (art. 19 LGPD).")
      setForm({ full_name: "", email: "", phone: "", request_type: "access", description: "" })
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="bg-white dark:bg-slate-800 border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg">itaCondo</Link>
          <Link to="/"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" /> Voltar</Button></Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-2xl space-y-6">
        <div className="text-center mb-4">
          <div className="inline-flex w-16 h-16 rounded-full bg-blue-100 items-center justify-center mb-4">
            <ShieldCheck className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">Encarregado de Dados (DPO)</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-2">Canal oficial para exercer seus direitos previstos no art. 18 da LGPD.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Contato direto</CardTitle>
            <CardDescription>Você também pode escrever para o e-mail abaixo.</CardDescription>
          </CardHeader>
          <CardContent>
            <a href={`mailto:${DPO_EMAIL}`} className="text-blue-600 underline font-medium">{DPO_EMAIL}</a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Formulário de solicitação</CardTitle>
            <CardDescription>Responderemos em até 15 dias.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome completo *</Label>
                  <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required maxLength={150} />
                </div>
                <div className="space-y-2">
                  <Label>E-mail *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required maxLength={200} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefone (opcional)</Label>
                <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} maxLength={30} />
              </div>
              <div className="space-y-2">
                <Label>Tipo de solicitação *</Label>
                <Select value={form.request_type} onValueChange={(v) => setForm({...form, request_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REQUEST_TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Descreva sua solicitação</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={5} maxLength={2000} />
              </div>
              <Button type="submit" disabled={loading} className="w-full">{loading ? "Enviando..." : "Enviar solicitação"}</Button>
            </form>
          </CardContent>
        </Card>
      </main>

      <PublicFooter />
    </div>
  )
}

export default Encarregado
